// ============================================================
// UnsavedChangesGuardContext.js
//
// Cross-component unsaved-changes navigation guard (the backlog's
// "CareAudit — global unsaved-changes guard across sidebar / header
// nav" item, built as the ratified React-context approach — the app
// uses <BrowserRouter>, so react-router's useBlocker is unavailable).
//
// Shape:
//   - A page with unsaved state REGISTERS a guard:
//       useEffect(() => unsavedGuard.register({
//         isDirty: () => boolean,          // consulted at nav time
//         prompt:  (target) => void,       // open the page's own dialog;
//       }), [unsavedGuard]);               //   target is string | function
//     The page's dialog resolves the prompt itself: "Stay" discards the
//     target, "Leave" either navigate(target) (string) or target()
//     (function).
//   - Nav sites (ManagerApp sidebar, Header account menu) consult it:
//       guardedRun(proceed)      — run proceed() now, unless a dirty
//                                  guard is registered, in which case
//                                  hand proceed to the guard's prompt.
//       isBlocked() / prompt(t)  — the lower-level pair, for Link
//                                  interception (preventDefault + prompt).
//
// No provider (or no registered guard, or a clean guard) → everything
// is a pass-through no-op, so shared components (Header) can consult
// the hook unconditionally on every surface. At most ONE guard is
// registered at a time (last registration wins; unregister is
// identity-checked so an unmount never clobbers a newer guard).
// ============================================================

import React, { createContext, useContext, useMemo, useRef } from 'react';

const UnsavedChangesGuardContext = createContext(null);

export function UnsavedChangesGuardProvider({ children }) {
  const guardRef = useRef(null);

  const api = useMemo(() => ({
    register(guard) {
      guardRef.current = guard;
      return () => {
        if (guardRef.current === guard) guardRef.current = null;
      };
    },
    isBlocked() {
      const g = guardRef.current;
      try {
        return !!(g && g.isDirty());
      } catch {
        // A throwing isDirty must never brick navigation.
        return false;
      }
    },
    prompt(target) {
      const g = guardRef.current;
      if (g) g.prompt(target);
    },
  }), []);

  return (
    <UnsavedChangesGuardContext.Provider value={api}>
      {children}
    </UnsavedChangesGuardContext.Provider>
  );
}

const NOOP_UNREGISTER = () => {};

export function useUnsavedChangesGuard() {
  const ctx = useContext(UnsavedChangesGuardContext);
  return useMemo(() => ({
    register: ctx ? ctx.register : () => NOOP_UNREGISTER,
    isBlocked: ctx ? ctx.isBlocked : () => false,
    prompt: ctx ? ctx.prompt : () => {},
    guardedRun(proceed) {
      if (ctx && ctx.isBlocked()) {
        ctx.prompt(proceed);
        return false;
      }
      proceed();
      return true;
    },
  }), [ctx]);
}
